class AnalyticsServiceHomepageHelper extends AnalyticsService {
  constructor() {
    super();
    this.device = window.innerWidth < 768 ? "mobile" : "desktop";
    this.deviceSuffix = this.device === "mobile" ? "Mobile" : "";
    this.heroEl = document.querySelector("[data-hero='true']");

    this.visualSectionIndex = 0; // Tracks image/video sections
    this.productRowIndex = 0; // Tracks link_cards sections

    this.observedSections = new Set();
    this.initHeroClick();

    this.initGlobalSectionTracking(), 1000;
  }
  // HELPERS
  buildAnalyticsEvent(
    eventName,
    {
      sectionIndex,
      section,
      sectionType,
      sectionId,
      contentName,
      handle,
      secondary,
    }
  ) {
    return new CustomEvent(eventName, {
      detail: {
        content_section_index: sectionIndex || null,
        content_section: section,
        content_section_type: sectionType || "unknown",
        content_section_id: sectionId,
        content_name: this.extractImageName(contentName),
        handle: handle || "unknown",
        secondary,
      },
    });
  }

  extractImageName(url) {
    return url?.split("/").pop()?.split(".")[0] || "unknown";
  }
  getNoSuffix(link) {
    if (link.dataset.twoImageSectionFirst === "true") return "1";
    if (link.dataset.twoImageSectionFirst === "false") return "2";
    return "";
  }

  getContentName(dataset, noSuffix) {
    return dataset[`aContentName${noSuffix}${this.deviceSuffix}`];
  }

  getHandle(dataset, noSuffix) {
    return dataset[`aHandle${noSuffix}${this.deviceSuffix}`];
  }

  // CORE FUNCTIONS
  // HERO
  initHeroClick() {
    if (!this.heroEl) return;

    const imageLinks = this.heroEl.querySelectorAll(`.${this.device}-link`);
    if (!imageLinks.length) return;

    imageLinks.forEach((link) => {
      link.addEventListener("click", () => {
        // Needed for two image section heros
        let noSuffix = this.getNoSuffix(link);
        if (!noSuffix) {
          noSuffix = "";
        }
        const heroDataset = this.heroEl.dataset;
        const contentName = this.getContentName(heroDataset, noSuffix);

        const handle = this.getHandle(heroDataset, noSuffix);

        if (!contentName || !handle) {
          console.error("Missing content name or handle for hero image");
          return;
        }

        const event = this.buildAnalyticsEvent("analytics:homeHeroClick", {
          section: "home_hero_media",
          sectionType: heroDataset.aSectionType || "unknown",
          sectionId: heroDataset.aSectionId,
          contentName: contentName,
          handle: handle,
        });

        document.dispatchEvent(event);
      });
    });
  }
  // This function is called to track the sections on the homepage
  // it is currently setup to track the sections that are not the hero section
  // - please ensure all sections have the appropriate analytics data attributes
  // GENERAL SECTION TRACKING
  getHomepageSections() {
    const templateEl = document.querySelector("[data-template-home]");
    const sections = [];

    if (templateEl) {
      let current = templateEl.parentElement.nextElementSibling;
      while (current) {
        if (current.classList.contains("shopify-section")) {
          sections.push(current);
        }
        current = current.nextElementSibling;
      }
    }

    return sections;
  }

  trackSectionClicks(index, section, dataset, isTwoImageSection) {
    const imageLinks = section.querySelectorAll(
      `.${this.device}-link, a.product-card`
    );

    imageLinks.forEach((link) => {
      link.addEventListener("click", () => {
        const noSuffix = this.getNoSuffix(link);
        // special case for link_cards sections
        let contentName, handle;
        if (dataset.aSectionType === "link_cards") {
          const productData = link.closest("[data-product-handle]")?.dataset;
          contentName = productData?.productName || "unknown";
          handle = productData?.productHandle;
        } else if (dataset.aSectionType === "gallery_section") {
          const galleryAElement = link.closest("[data-a-handle]");

          contentName = this.getContentName(galleryAElement.dataset, noSuffix);
          handle = this.getHandle(galleryAElement.dataset, noSuffix);
        } else {
          contentName = this.getContentName(dataset, noSuffix);
          handle = this.getHandle(dataset, noSuffix);
        }

        if (!contentName || !handle) {
          console.error("Missing content name or handle for image link");
          return;
        }

        const event = this.buildAnalyticsEvent("analytics:homeSectionClick", {
          sectionIndex: isTwoImageSection && noSuffix === "2" ? index + 1 : index,
          section:
            dataset.sectionType == "link_cards"
              ? "home_product_row"
              : "home_media_section",
          sectionType: dataset.aSectionType || "unknown",
          sectionId: dataset.aSectionId || section.id,
          contentName: contentName,
          handle: handle,
          secondary: isTwoImageSection && noSuffix === "2",
        });

        document.dispatchEvent(event);
      });
    });
  }

  observeSectionView(index, section, sectionId, aElement, isTwoImageSection) {

    const makeObserver = (targetEl, suffix) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const uniqueKey = `${sectionId}-${suffix}`;
            if (this.observedSections.has(uniqueKey)) return;
            this.observedSections.add(uniqueKey);

            if (!aElement) {
              console.error("Missing data-a-section-id in section", sectionId);
              return;
            }

            const noSuffix = suffix.toString();
            const contentName = this.getContentName(aElement.dataset, noSuffix);
            const handle = this.getHandle(aElement.dataset, noSuffix);
            const isSecondary = isTwoImageSection && suffix === 2;

            const event = this.buildAnalyticsEvent(
              "analytics:homeSectionView",
              {
                sectionIndex: isSecondary ? index + 1 : index,
                section: "home_section",
                sectionType: aElement.dataset.aSectionType || "unknown",
                sectionId: aElement.dataset.aSectionId || sectionId,
                contentName: this.extractImageName(contentName),
                handle: handle,
                secondary: isSecondary || false,
              }
            );
            document.dispatchEvent(event);
          }
        },
        { threshold: 0.5 }
      );

      observer.observe(targetEl);
    };

    if (isTwoImageSection) {
      const children = section.querySelectorAll(".image-wrapper");
      if (children[0]) makeObserver(children[0], 1);
      if (children[1]) makeObserver(children[1], 2);
    } else {
      // Observe the parent itself
      makeObserver(section, this.getNoSuffix(aElement));
    }
  }

  initGlobalSectionTracking() {
    const sections = this.getHomepageSections();

    sections.forEach((section) => {
      const aElement = section.querySelector("[data-a-section-id]");
      if (!aElement || aElement.dataset.hero === "true") return;

      const sectionId = section.id;
      const dataset = aElement.dataset;
      const type = dataset.aSectionType;

      let localVisualIndex = null;
      let localProductIndex = null;

      if (
        [
          "single_image_section",
          "two_image_section",
          "upload_video_section",
          "gallery_section",
        ].includes(type)
      ) {
        this.visualSectionIndex++;

        localVisualIndex = this.visualSectionIndex;
      } else if (type === "link_cards") {
        this.productRowIndex++;
        localProductIndex = this.productRowIndex;
      }
      const localIndex =
        type == "link_cards" ? localProductIndex : localVisualIndex;
      const isTwoImageSection = type === "two_image_section";
      this.trackSectionClicks(localIndex, section, dataset, isTwoImageSection);
      this.observeSectionView(localIndex, section, sectionId, aElement, isTwoImageSection);
      // if two_image_section increment again to account for secondary image
      if (type === "two_image_section") this.visualSectionIndex++;
    });
  }
}
